import { Link, useLocation } from "react-router-dom";
import { useState } from "react";
import { ShieldCheck, Menu, X, Phone, Heart, Users } from "lucide-react";
import { Button } from "@/components/ui/button";

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { path: "/", label: "Home" },
    { path: "/report", label: "Report Scam" },
    { path: "/track", label: "Track Case" },
    { path: "/knowledge", label: "Help Guide" },
  ];

  const ctaLinks = [
    { path: "/volunteer", label: "Volunteer", icon: Users, variant: "outline" },
    { path: "/donate", label: "Donate", icon: Heart, variant: "primary" },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="sticky top-0 z-50 bg-white shadow-md border-b border-slate-200" data-testid="navbar">
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-3 text-[#2c3e50] hover:opacity-80 transition-opacity"
            data-testid="navbar-logo"
          >
            <ShieldCheck className="w-10 h-10 text-[#e74c3c]" />
            <div className="hidden sm:block">
              <span className="text-xl font-bold font-['Manrope']">PEFF</span>
              <p className="text-xs text-[#546e7a] -mt-1">Elderra's Scam Tracker</p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-4">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-base font-medium transition-colors py-2 px-3 rounded-lg ${
                  isActive(link.path)
                    ? "text-[#e74c3c] bg-red-50"
                    : "text-[#2c3e50] hover:text-[#e74c3c] hover:bg-slate-50"
                }`}
                data-testid={`nav-link-${link.label.toLowerCase().replace(' ', '-')}`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center gap-3">
            <Link to="/volunteer" data-testid="nav-volunteer-btn">
              <Button 
                variant="outline" 
                className="border-2 border-[#2c3e50] text-[#2c3e50] font-semibold hover:bg-slate-50"
              >
                <Users className="w-4 h-4 mr-2" />
                Volunteer
              </Button>
            </Link>
            <Link to="/donate" data-testid="nav-donate-btn">
              <Button className="bg-[#e74c3c] hover:bg-[#c0392b] text-white font-bold">
                <Heart className="w-4 h-4 mr-2" />
                Donate
              </Button>
            </Link>
            <a
              href="tel:1930"
              className="flex items-center gap-2 bg-[#2c3e50] text-white px-4 py-2 rounded-lg font-bold hover:bg-[#1a252f] transition-colors"
              data-testid="emergency-helpline"
            >
              <Phone className="w-4 h-4" />
              <span>1930</span>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-[#2c3e50]"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label={isMenuOpen ? "Close menu" : "Open menu"}
            data-testid="mobile-menu-btn"
          >
            {isMenuOpen ? <X className="w-8 h-8" /> : <Menu className="w-8 h-8" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden py-4 border-t border-slate-200" data-testid="mobile-menu">
            <div className="flex flex-col gap-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-lg font-medium py-3 px-4 rounded-lg ${
                    isActive(link.path)
                      ? "text-[#e74c3c] bg-red-50"
                      : "text-[#2c3e50] hover:bg-slate-50"
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                  data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(' ', '-')}`}
                >
                  {link.label}
                </Link>
              ))}
              
              <div className="border-t border-slate-200 my-2 pt-2">
                <Link
                  to="/volunteer"
                  className="flex items-center gap-2 text-lg font-medium py-3 px-4 rounded-lg text-[#2c3e50] hover:bg-slate-50"
                  onClick={() => setIsMenuOpen(false)}
                  data-testid="mobile-nav-volunteer"
                >
                  <Users className="w-5 h-5" />
                  Volunteer Signup
                </Link>
                <Link
                  to="/donate"
                  className="flex items-center gap-2 text-lg font-medium py-3 px-4 rounded-lg text-[#e74c3c] hover:bg-red-50"
                  onClick={() => setIsMenuOpen(false)}
                  data-testid="mobile-nav-donate"
                >
                  <Heart className="w-5 h-5" />
                  Donate Now
                </Link>
              </div>
              
              <a
                href="tel:1930"
                className="flex items-center justify-center gap-2 bg-[#e74c3c] text-white py-3 px-4 rounded-lg font-bold mt-2"
                data-testid="mobile-emergency-helpline"
              >
                <Phone className="w-5 h-5" />
                <span>Call 1930 Helpline</span>
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
